import { Button, Card, Col, Divider, Flex, Modal, Progress, Row, Table, TableProps } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

// components
import PageTitle from 'components/title/title';
import BackButton from 'components/backButton/backButton';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import StatusComponent from 'components/status/status';
import DisabledElement from 'components/disabledElement/disabledElement';
import Riyal from 'components/riyal/riyal';
import VerificationModal from '../../verificationModal';
import pdf from 'assets/images/pdf.png';

// services
import NotificationService from 'core/services/notification';
import {
	onGetLoyaltyProgramById,
	onCancelLoyaltyProgram,
	onDistributeLoyaltyProgram,
	onCancelDistributionLoyaltyProgram,
} from '../services/loyaltyProgramService';

// models
import { LoyalityDistribution, LoyaltyProgram } from '../models/loyaltyProgram';
import { VerificationPayload } from '../../commonModels';
import { isRTL } from 'core/services/language';
import { StatusColors } from 'utils/statusColors';
import { dateFormat } from 'utils/dateFormat';
import ListButton from 'components/listButton/listButton';
import { formatErrorMessages } from 'utils/errorMessage';
import { downloadFile } from 'utils/downloadFile';

// hooks
import useCompanySuspended from 'core/hooks/useCompanySuspended';

const LoyaltyProgramViewPage: FunctionComponent = () => {
	const { t } = useTranslation();

	const { suspendedProps } = useCompanySuspended();

	const navigate = useNavigate();
	const { id } = useParams();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [program, setProgram] = useState<LoyaltyProgram>();

	const [selectedDistribution, setSelectedDistribution] = useState<LoyalityDistribution>();
	const [distributionAction, setDistributionAction] = useState<'distribute' | 'cancel'>('distribute');
	const [isActioning, setIsActioning] = useState<boolean>(false);

	useEffect(() => {
		getLoyaltyProgramDetails();
	}, []);

	const getLoyaltyProgramDetails = async () => {
		try {
			const response = await onGetLoyaltyProgramById(Number(id));
			setProgram(response);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			navigate('/dashboard/actions/loyalty-programs');
		} finally {
			setIsLoading(false);
		}
	};

	const onCancelProgram = () => {
		Modal.confirm({
			title: t('ACTIONS.CONFIRM_GRANT_CANCELLATION'),
			content: t('ACTIONS.CONFIRM_GRANT_CANCELLATION_DESC'),
			okText: t('COMMON.CANCEL'),
			okButtonProps: { danger: true },
			cancelText: t('COMMON.BACK'),
			onOk: async () => {
				try {
					await onCancelLoyaltyProgram(Number(id));
					NotificationService.success(t('COMMON.SUCCESS'), t('COMMON.SUCCESS'));
					navigate('/dashboard/actions/loyalty-programs');
				} catch (error) {
					const message = formatErrorMessages(error);
					NotificationService.error(t('COMMON.ERROR'), message || t('COMMON.SUBMIT_ERROR'));
				}
			},
		});
	};

	const openDistributionModal = (distribution: LoyalityDistribution, action: 'distribute' | 'cancel') => {
		setSelectedDistribution(distribution);
		setDistributionAction(action);
	};

	const distributionModalTitle = () => {
		if (distributionAction === 'cancel') return isOption ? t('ACTIONS.CONFIRM_OPTION_REFUSAL') : t('ACTIONS.CONFIRM_GRANT_CANCELLATION');
		return isOption ? t('ACTIONS.CONFIRM_OPTION_GRANT') : t('ACTIONS.CONFIRM_GRANT');
	};

	const distributionModalNote = () => {
		if (distributionAction === 'cancel') return t('ACTIONS.CONFIRM_GRANT_CANCELLATION_DESC');
		if (!isOption) return t('ACTIONS.CONFIRM_GRANT_DESC');
		return program?.salary_deduction ? t('ACTIONS.CONFIRM_OPTION_GRANT_SALARY_DESC') : t('ACTIONS.CONFIRM_OPTION_GRANT_APP_DESC');
	};

	const onDistributionVerified = async ({ method, key }: VerificationPayload) => {
		if (!selectedDistribution) return;
		try {
			setIsActioning(true);
			const payload = { verified_using: method, [method]: key };
			if (distributionAction === 'distribute') await onDistributeLoyaltyProgram(selectedDistribution.id, payload);
			else await onCancelDistributionLoyaltyProgram(selectedDistribution.id, payload);

			setSelectedDistribution(undefined);
			await getLoyaltyProgramDetails();
		} catch (error) {
			const message = formatErrorMessages(error);
			NotificationService.error(t('COMMON.ERROR'), message || t('COMMON.SUBMIT_ERROR'));
		} finally {
			setIsActioning(false);
		}
	};

	const translated = (field?: { ar: string; en: string }) => (field ? (isRTL() ? field.ar : field.en) : '');

	const isOption = program?.incentive_type === 'Option';
	const isPeriodic = program?.restriction_type === 'Periodic';
	const isRestrictedByDate = program?.restriction_type === 'RestrictedByDate';
	const isCancellable = program ? ['PendingAcceptance', 'Scheduled', 'Ongoing'].includes(program.status) : false;

	const progressPercent = program && program.number_of_shares > 0 ? Math.round((program.distributed_shares / program.number_of_shares) * 100) : 0;

	const pricePerShare = program?.price_per_share ?? 0;
	const paidAmount = (program?.distributed_shares ?? 0) * pricePerShare;
	const remainingAmount = (program?.remaining_shares ?? 0) * pricePerShare;

	const programTypeLabel = () => {
		if (!program) return '';
		const base = isOption ? t('ACTIONS.OPTION_SHARES') : t('ACTIONS.GRANT_SHARES');
		const periodic = isOption ? t('ACTIONS.PERIODIC_OPTION_SHARES') : t('ACTIONS.PERIODIC_GRANT_SHARES');
		return isPeriodic ? periodic : base;
	};

	const distributionColumns: TableProps<LoyalityDistribution>['columns'] = [
		{ title: t('ACTIONS.ORDER'), dataIndex: 'installment_number', key: 'installment_number', width: 70 },
		{
			title: t('ACTIONS.GRANT_DATE'),
			dataIndex: 'distribution_date',
			key: 'distribution_date',
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'translated_status',
			key: 'status',
			render: (_, record) => <StatusComponent small text={translated(record.translated_status)} color={StatusColors[record.status] ?? 'orange'} />,
		},
		{
			title: t('COMMON.SHARES'),
			dataIndex: 'number_of_shares',
			key: 'number_of_shares',
			render: (value) => <DisabledElement value={t('ACTIONS.NUM_SHARES', { num: value })} style={{ marginBottom: 0 }} />,
		},
	];

	if (isOption) {
		distributionColumns.push({
			title: t('ACTIONS.PAYMENT'),
			key: 'payment',
			render: (_, record) => (
				<DisabledElement value={((program?.price_per_share ?? 0) * record.number_of_shares).toFixed(2)} style={{ marginBottom: 0 }} />
			),
		});
	}

	distributionColumns.push({
		title: t('COMMON.ACTIONS'),
		key: 'actions',
		render: (_, record) =>
			record.is_actionable ? (
				<Flex gap={10} align="center" justify="center">
					<ListButton type="accept" {...suspendedProps} onClick={() => openDistributionModal(record, 'distribute')} />
					<ListButton type="reject" {...suspendedProps} onClick={() => openDistributionModal(record, 'cancel')} />
				</Flex>
			) : (
				<StatusComponent small text={translated(record.translated_status)} color={StatusColors[record.status] ?? 'orange'} />
			),
	});

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton />

			{isLoading || !program ? (
				<LoadingComponent />
			) : (
				<Card>
					<Row gutter={[16, 16]} justify="space-between">
						{/* Header */}
						<Col span={24} md={16}>
							<Flex align="center" gap={15} className="mb-2">
								<h5 className="mb-0 fw-medium">{program.request_number}</h5>
								<StatusComponent small color={StatusColors[program.status] ?? 'orange'} text={translated(program.translated_status)} />
							</Flex>
							<p className="mb-0 text-light">{dateFormat(program.created_at)}</p>
						</Col>

						<Col span={24} md={8} lg={6} xxl={5}>
							{program?.contract_pdf_url && (
								<Flex align="center" justify="space-between" gap={20} wrap className="mb-3 containerDiv dashed">
									<Flex align="center" gap={10}>
										<img src={pdf} width={40} />
										<h6 className="fw-medium mb-0">{t('COMMON.CONTRACT')}</h6>
									</Flex>
									<Button size="middle" type="primary" onClick={()=> downloadFile(program.contract_pdf_url!, `${program.request_number}.pdf`)}>
										{t('ACTIONS.DOWNLOAD_FILE')}
									</Button>
								</Flex>
							)}

							<Flex justify="space-between" gap={10} wrap>
								<h6 className="mb-0">{t('ACTIONS.PROGRESS')}</h6>
								<Flex vertical align="center" justify="space-between" gap={10}>
									<Progress percent={progressPercent} size={[20, 25]} steps={10} showInfo={false} />
									<h6 className="mb-0 text-center">{progressPercent}%</h6>
								</Flex>
							</Flex>
						</Col>

						<Col span={24}>
							<Flex justify="space-between" align="flex-start">
								<div>
									<h6 className="fw-medium mb-1">{t('ACTIONS.PROGRAM_TYPE')}</h6>
									<h6 className="mb-0">{programTypeLabel()}</h6>
								</div>
							</Flex>
						</Col>

						<Col span={24}>
							<Divider className="my-1" />
						</Col>

						{/* Beneficiary */}
						<Col span={24} md={isPeriodic ? 8 : 12}>
							<DisabledElement label={t('ACTIONS.BENEFICIARY_NATIONAL_ID')} value={program.beneficiary_national_id} />
						</Col>

						<Col span={24} md={isPeriodic ? 8 : 12}>
							<DisabledElement
								label={t('ACTIONS.BENEFICIARY_NAME')}
								value={translated(program.translated_beneficiary_name) || program.beneficiary_name}
							/>
						</Col>

						{/* Payment method – periodic option */}
						{isPeriodic && isOption && (
							<Col span={24} md={8}>
								<h6 className="mb-1">{t('ACTIONS.PAYMENT_METHOD')}</h6>
								{program.salary_deduction ? (
									<>
										<h6 className="mb-0" style={{ color: '#e5484d' }}>
											{t('ACTIONS.SALARY_DEDUCTION')}
										</h6>
										<p className="mb-0" style={{ color: '#e5484d' }}>
											{t('ACTIONS.SALARY_DEDUCTION_DESC')}
										</p>
									</>
								) : (
									<>
										<h6 className="mb-0" style={{ color: '#00b96b' }}>
											{t('ACTIONS.EMPLOYEE_PAYS_THROUGH_APP')}
										</h6>
										<p className="mb-0" style={{ color: '#00b96b' }}>
											{t('ACTIONS.EMPLOYEE_PAYS_THROUGH_APP_DESC')}
										</p>
									</>
								)}
							</Col>
						)}

						<Col span={24}>
							<Divider className="my-1" />
						</Col>

						{/* PERIODIC layout: stats + distributions table */}
						{isPeriodic ? (
							<>
								<Col span={24} md={5}>
									<Flex vertical gap={16}>
										<div>
											<DisabledElement
												label={t('ACTIONS.PROGRAM_SHARES')}
												value={program.number_of_shares}
												style={{ marginBottom: 0 }}
												addonAfter={<span className="me-2">{t('COMMON.SHARES')}</span>}
											/>
										</div>

										<div>
											<DisabledElement
												label={t('ACTIONS.SHARES_GRANTED')}
												value={program.distributed_shares}
												style={{ marginBottom: 0 }}
												addonAfter={<span className="me-2">{t('COMMON.SHARES')}</span>}
											/>
										</div>
										<div>
											<DisabledElement
												label={t('ACTIONS.SHARES_NOT_GRANTED')}
												value={program.remaining_shares}
												style={{ marginBottom: 0 }}
												addonAfter={<span className="me-2">{t('COMMON.SHARES')}</span>}
											/>
										</div>
										{isOption && (
											<>
												<DisabledElement
													label={t('ACTIONS.PROGRAM_TOTAL_VALUE')}
													value={program.total_price?.toFixed(2)}
													style={{ marginBottom: 0 }}
													showPrice
												/>

												<DisabledElement label={t('ACTIONS.PAID_AMOUNT')} value={paidAmount.toFixed(2)} style={{ marginBottom: 0 }} showPrice />

												<DisabledElement label={t('ACTIONS.REMAINING_AMOUNT')} value={remainingAmount.toFixed(2)} style={{ marginBottom: 0 }} showPrice />
											</>
										)}
									</Flex>
								</Col>
							</>
						) : (
							<>
								{/* NON-PERIODIC layout */}
								<Col span={24} md={isOption ? 6 : 8}>
									<DisabledElement label={isOption ? t('ACTIONS.NUMBER_OF_SHARES') : t('ACTIONS.SHARES_GRANTED')} value={program.number_of_shares} />
								</Col>

								{isOption && (
									<Col span={24} md={6}>
										<DisabledElement
											label={t('COMMON.PRICE')}
											value={program.price_per_share ?? 0}
											addonAfter={
												<span className="me-2">
													<Riyal style={{ marginTop: -4 }} /> / {t('ACTIONS.SHARE')}
												</span>
											}
										/>
									</Col>
								)}

								{isRestrictedByDate && (
									<Col span={24} md={6}>
										<DisabledElement label={t('ACTIONS.ELIGIBILITY_DATE')} value={dateFormat(program.eligibility_date, true)} />
									</Col>
								)}

								{isOption && (
									<Col span={24} md={6}>
										<DisabledElement label={t('ACTIONS.PAYMENT')} value={(program.total_price ?? 0).toFixed(2)} />
									</Col>
								)}
							</>
						)}

						{program.distributions && program.distributions.length > 0 && (
							<Col span={24} md={19}>
								<Table rowKey="id" columns={distributionColumns} dataSource={program.distributions} pagination={false} size="small" />
							</Col>
						)}

						{/* Footer: program total value + cancel */}
						<Col span={24}>
							<Divider className="my-1" />
							<Flex justify="flex-end" align="center" wrap="wrap" gap={16}>
								{isCancellable && (
									<Button type="primary" danger onClick={onCancelProgram}>
										{t('COMMON.CANCEL')}
									</Button>
								)}
							</Flex>
						</Col>
					</Row>
				</Card>
			)}

			<VerificationModal
				open={!!selectedDistribution}
				title={distributionModalTitle()}
				note={distributionModalNote()}
				isLoading={isActioning}
				id={String(selectedDistribution?.id ?? '')}
				onCancel={() => setSelectedDistribution(undefined)}
				onVerificationSuccess={onDistributionVerified}
				module={distributionAction === 'distribute' ? 'loyalty_program_distribute' : 'loyalty_program_cancel_distribution'}
			/>
		</>
	);
};

export default LoyaltyProgramViewPage;
